/**
 * Single source of truth for turning a saved scenario's actions into the maps
 * `enterEditMode` consumes.
 *
 * Four surfaces auto-enter edit mode from a saved scenario — PlayerPanel,
 * ActionMenu, ActionsSummaryPanel and TransitionsSummaryCard — and each used
 * to derive these maps inline. That copy-paste is exactly how PlayerPanel
 * drifted into seeding empty maps and wiping stored financial details
 * (SE-5567), so the derivation lives here once.
 * @module features/squad-planning/utils/buildScenarioMaps
 */

import type { SquadPlanningGetScenarioQuery } from "@/generated/graphql";

import type { SignedPlayerData } from "@/features/squad-planning/stores/scenarioStore";
import type {
  ActionDetails,
  PlayerAction,
  SquadPlayer,
} from "@/features/squad-planning/types";
import { mapTransitionToAction } from "./mapTransitionToAction";

/** A single persisted action as returned by the getScenario query. */
export type ScenarioAction =
  SquadPlanningGetScenarioQuery["getScenario"]["actions"][number];

/**
 * A persisted action whose `playerId` is present.
 *
 * The planning-mode draft is KEYED by player id, and `getDraftPayload` sends
 * each key straight back as the action's `playerId`. So an action with a null
 * id cannot round-trip: bucketing several under `""` would collapse them into
 * one entry AND re-send `playerId: ""`. These builders therefore skip them.
 * The app never creates one — every draft action is keyed by a real search
 * result id — but the schema marks `playerId` nullable, so the guard is real.
 */
type IdentifiedAction = ScenarioAction & { readonly playerId: string };

/**
 * Narrows to actions that carry a usable player id.
 * @param actions - Persisted scenario actions
 * @returns Only the actions with a non-empty `playerId`
 */
function withPlayerId(
  actions: readonly ScenarioAction[] | null | undefined
): readonly IdentifiedAction[] {
  return (actions ?? []).filter(
    (a): a is IdentifiedAction => a.playerId != null && a.playerId !== ""
  );
}

/**
 * Builds the player-id → action map from roster players' `transitionType`.
 *
 * Takes only `id`/`transitionType` (SE-8346), not a full `SquadPlayer` —
 * that's every field this function ever reads, and it lets a caller pass
 * raw, unformatted rows (e.g. Targets) without paying to format the rest of
 * each row's fields first.
 * @param players - Rows carrying `id` and `transitionType`
 * @returns Action map keyed by player id
 */
export function buildActionsMapFromPlayers(
  players: readonly Pick<SquadPlayer, "id" | "transitionType">[]
): Readonly<Record<string, PlayerAction>> {
  return Object.fromEntries(
    players
      .map(p => [p.id, mapTransitionToAction(p.transitionType)] as const)
      .filter((e): e is [string, PlayerAction] => e[1] != null)
  );
}

/**
 * Builds the player-id → action map from a saved scenario's own actions.
 * Differs from {@link buildActionsMapFromPlayers} in that it covers actions
 * for players absent from the loaded roster (e.g. external signings).
 * @param actions - Persisted scenario actions
 * @returns Action map keyed by player id
 */
export function buildActionsMapFromScenario(
  actions: readonly ScenarioAction[] | null | undefined
): Readonly<Record<string, PlayerAction>> {
  return Object.fromEntries(
    withPlayerId(actions)
      .map(a => [a.playerId, mapTransitionToAction(a.transitionType)] as const)
      .filter((e): e is [string, PlayerAction] => e[1] != null)
  );
}

/**
 * Builds the player-id → persisted financial details map.
 * @param actions - Persisted scenario actions
 * @returns Details map keyed by player id
 */
export function buildScenarioDetailsMap(
  actions: readonly ScenarioAction[] | null | undefined
): Readonly<Record<string, ActionDetails>> {
  return Object.fromEntries(
    withPlayerId(actions)
      .map(a => {
        const action = mapTransitionToAction(a.transitionType);
        if (action == null) return null;
        const fin = a.financialDetails;
        return [
          a.playerId,
          {
            action,
            salePrice: fin?.salePrice ?? null,
            loanFee: fin?.loanFee ?? null,
            transferFee: fin?.signingFee ?? null,
            annualSalary: fin?.wage ?? null,
            durationMonths: fin?.durationMonths ?? null,
            signingBonus: fin?.signingBonus ?? null,
            agentFee: fin?.agentFee ?? null,
            // Absent stays null — never defaulted here. A legacy loan with no
            // recorded share must round-trip as "none", and the domain
            // default is applied only at the live-impact seam (SE-7324).
            coveredSharePercent: fin?.coveredSharePercent ?? null,
          },
        ] as const;
      })
      .filter((e): e is NonNullable<typeof e> => e != null)
  );
}

/**
 * Builds the player-id → display data map for persisted Sign actions.
 *
 * Covers EVERY persisted Sign action, not just the ones carrying
 * `externalPlayerData`. A player signed through the search modal who happens
 * to have a system record is stored as a plain transition with a `playerId`
 * and no `externalPlayerData` (the SE-5596 input contract), and if he is not
 * on the org's roster or targets then nothing else in a cold reload knows his
 * name — `useSquadPlanningPlayers` flattens only firstTeam/academy/targets.
 * Dropping him here is what rendered him as an "Unknown" row with dashed-out
 * attributes after a full browser reload (SE-6519). `getScenario` already
 * resolves `playerName` for exactly this reason, so it is the fallback.
 * @param actions - Persisted scenario actions
 * @returns Signed-player map keyed by player id
 */
export function buildScenarioSignedPlayersMap(
  actions: readonly ScenarioAction[] | null | undefined
): Readonly<Record<string, SignedPlayerData>> {
  return Object.fromEntries(
    withPlayerId(actions)
      .filter(a => mapTransitionToAction(a.transitionType) === "Sign")
      .map(a => {
        const ext = a.externalPlayerData;
        return [
          a.playerId,
          {
            displayName: ext?.name ?? a.playerName ?? "",
            teamName: ext?.teamName ?? null,
            positions: (ext?.positions ?? []).join(", "),
            age: ext?.age ?? null,
            gprScore: ext?.gpr ?? null,
            nationalityName: ext?.nationalityName ?? null,
          },
        ] as const;
      })
  );
}
